import { Components } from '@/api/generated/api';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { getHasProviderAccess, getHasTenantAdminAccess, getHasTenantOwnerAccess } from '@/utils/role.utils';
import { computed, ComputedRef } from 'vue';

interface IPermissionsComposable {
  canManageAccessRights: ComputedRef<boolean>;
  canManageCompetences: ComputedRef<boolean>;
  canManagePerformanceAssessments: ComputedRef<boolean>;
  canManageStudents: ComputedRef<boolean>;
  canManageStudentsForClass: (schoolClassId?: number | null) => boolean;
  hasProviderAccess: ComputedRef<boolean>;
  hasTenantAdminAccess: ComputedRef<boolean>;
  hasTenantOwnerAccess: ComputedRef<boolean>;
  canAddCompetences: ComputedRef<boolean>;
  hasTenantOwnerOrAdminAccess: ComputedRef<boolean>;
}

export function usePermissions(): IPermissionsComposable {
  const { whoAmI } = useWhoAmI();
  const { currentClass } = useCurrentClass();

  const hasProviderAccess = computed(() => getHasProviderAccess(whoAmI.value));
  const hasTenantAdminAccess = computed(() => getHasTenantAdminAccess(whoAmI.value));
  const hasTenantOwnerAccess = computed(() => getHasTenantOwnerAccess(whoAmI.value));

  const checkPermission = (
    permissionKey: keyof Components.Schemas.AccessRightPermissionsDto,
    schoolClassId: number | null | undefined = currentClass.value?.id,
  ): boolean => {
    const targetSchoolClassId = schoolClassId ?? currentClass.value?.id;
    if (hasTenantAdminAccess.value) return true;
    if (!targetSchoolClassId) return false;
    return !!whoAmI.value?.userRoles.find((ur) =>
      ur.accessRights.some((ar) => ar.permissions[permissionKey] && ar.schoolClassId === targetSchoolClassId),
    );
  };

  const canManageAccessRights = computed(() => checkPermission('canManageAccessRights'));
  const canManageCompetences = computed(() => checkPermission('canManageCompetences'));
  const canManagePerformanceAssessments = computed(() => checkPermission('canManagePerformanceAssessments'));
  const canManageStudents = computed(() => checkPermission('canManageStudents'));
  const canManageStudentsForClass = (schoolClassId?: number | null): boolean =>
    schoolClassId != null && checkPermission('canManageStudents', schoolClassId);
  const canAddCompetences = computed(() => {
    if (hasTenantAdminAccess.value) return true;
    if (!currentClass.value?.id) return false;
    const accessRight = whoAmI.value?.userRoles
      .flatMap((ur) => ur.accessRights)
      .find((ar) => ar.schoolClassId === currentClass.value?.id);

    if (!accessRight) return false;

    const hasSubjectRestrictions = accessRight.permissions.subjects && accessRight.permissions.subjects.length > 0;

    return canManageCompetences.value && !hasSubjectRestrictions;
  });

  const hasTenantOwnerOrAdminAccess = computed(() => hasTenantAdminAccess.value || hasTenantOwnerAccess.value);

  return {
    canManageAccessRights,
    canManageCompetences,
    canManagePerformanceAssessments,
    hasProviderAccess,
    hasTenantAdminAccess,
    hasTenantOwnerAccess,
    canManageStudents,
    canManageStudentsForClass,
    canAddCompetences,
    hasTenantOwnerOrAdminAccess,
  };
}
